<template>
  <div class="reputation-view">
    <section class="site-hero reputation-hero" :style="heroStyle">
      <div class="site-hero__inner">
        <h1>客户满意度和评价</h1>
        <p v-for="(paragraph, index) in pageIntro" :key="index">{{ paragraph }}</p>
      </div>
    </section>

    <section class="page-section content-section">
      <div class="page-head">
        <div><h2>客户评价</h2></div>
      </div>
      <div class="page-shell reputation-layout">
        <aside class="score-panel panel-card">
          <h3>满意度调查表</h3>
          <div class="pdf-preview">
            <iframe
              title="满意度调查表第一页预览"
              :src="surveyPreviewUrl"
            ></iframe>
            <a class="pdf-link" :href="surveyFileUrl" target="_blank" rel="noopener">打开满意度调查表</a>
          </div>
          <!-- <div class="score-grid">
            <article><strong>Auto</strong><span>纵向自动滚动</span></article>
            <article><strong>Hover</strong><span>悬停停止滚动</span></article>
            <article><strong>Click</strong><span>点击播放视频</span></article>
            <article><strong>Loop</strong><span>循环展示内容</span></article>
          </div> -->
        </aside>
        <div
          class="review-stream"
          :class="{ 'review-stream--static': !shouldAnimateReviews }"
          @mouseenter="pauseReviewAnimation"
          @mouseleave="resumeReviewAnimation"
        >
          <div
            class="review-track"
            :style="reviewTrackStyle"
          >
            <article v-for="item in reputationVideos" :key="item._reviewKey" ref="reviewCards" class="review-card panel-card">
              <video
                ref="reviewVideos"
                class="review-video"
                controls
                autoplay
                muted
                loop
                playsinline
                preload="metadata"
                :src="resolveVideoUrl(item)"
              ></video>
              <div class="video-info">
                <h3>{{ item.title }}</h3>
                <span>{{ item.tag }}</span>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getSitePage } from "@/api/website";

export default {
  name: "ReputationView",
  data() {
    return {
      reputationVideos: [],
      reputationSurveyFiles: [],
      reviewAnimationFrame: null,
      reviewAnimationPaused: false,
      reviewLastTimestamp: 0,
      reviewScrollOffset: 0,
      pageSettings: {
        bannerImage: "/assets/banner.png",
        intro: []
      }
    };
  },
  computed: {
    shouldAnimateReviews() {
      return this.reputationVideos.length > 1;
    },
    reviewTrackStyle() {
      if (!this.shouldAnimateReviews) {
        return null;
      }
      return {
        transform: `translateY(-${this.reviewScrollOffset}px)`
      };
    },
    surveyFileUrl() {
      const item = this.reputationSurveyFiles.length ? this.reputationSurveyFiles[0] : null;
      return (item && item.poster) || "/assets/满意度调查表.pdf";
    },
    surveyPreviewUrl() {
      const url = this.surveyFileUrl.split("#")[0];
      return `${url}#page=1&view=FitH&toolbar=0&navpanes=0`;
    },
    pageIntro() {
      return this.pageSettings.intro && this.pageSettings.intro.length
        ? this.pageSettings.intro: [];
    },
    heroStyle() {
      const image = this.pageSettings.bannerImage || "/assets/banner.png";
      return {
        "--page-banner-image": `url("${image}")`
      };
    }
  },
  created() {
    getSitePage("reputation").then((res) => {
      const data = res.data || {};
      const pageSettings = data.pageSettings || {};
      this.pageSettings = pageSettings.reputation || this.pageSettings;
      this.reputationVideos = this.normalizeReviewVideos(data.reputationVideos || []);
      this.reputationSurveyFiles = data.reputationSurveyFiles || [];
      this.$nextTick(() => {
        this.playReviewVideos();
        this.startReviewAnimation();
      });
    });
  },
  beforeDestroy() {
    this.stopReviewAnimation();
  },
  methods: {
    normalizeReviewVideos(rows) {
      return (rows || []).map((item, index) => ({
        ...item,
        _reviewKey: `${item.key || item.id || item.poster || item.title || "review"}-${index}`
      }));
    },
    resolveVideoUrl(item) {
      return (item && item.poster) || "/assets/video.mp4";
    },
    startReviewAnimation() {
      this.stopReviewAnimation();
      this.reviewScrollOffset = 0;
      this.reviewLastTimestamp = 0;
      this.reviewAnimationPaused = false;
      if (!this.shouldAnimateReviews) {
        return;
      }
      this.reviewAnimationFrame = window.requestAnimationFrame(this.stepReviewAnimation);
    },
    stopReviewAnimation() {
      if (this.reviewAnimationFrame) {
        window.cancelAnimationFrame(this.reviewAnimationFrame);
        this.reviewAnimationFrame = null;
      }
      this.reviewLastTimestamp = 0;
      this.reviewScrollOffset = 0;
    },
    pauseReviewAnimation() {
      if (!this.shouldAnimateReviews) {
        return;
      }
      this.reviewAnimationPaused = true;
    },
    resumeReviewAnimation() {
      if (!this.shouldAnimateReviews) {
        return;
      }
      this.reviewAnimationPaused = false;
    },
    stepReviewAnimation(timestamp) {
      if (!this.shouldAnimateReviews) {
        this.stopReviewAnimation();
        return;
      }
      if (!this.reviewLastTimestamp) {
        this.reviewLastTimestamp = timestamp;
      }
      const deltaSeconds = (timestamp - this.reviewLastTimestamp) / 1000;
      this.reviewLastTimestamp = timestamp;
      if (!this.reviewAnimationPaused) {
        this.reviewScrollOffset += deltaSeconds * 26;
        const firstCardDistance = this.getFirstReviewCardDistance();
        if (firstCardDistance > 0 && this.reviewScrollOffset >= firstCardDistance) {
          const firstVideo = this.reputationVideos.shift();
          if (firstVideo) {
            this.reputationVideos.push(firstVideo);
          }
          this.reviewScrollOffset -= firstCardDistance;
          this.$nextTick(() => {
            this.playReviewVideos();
          });
        }
      }
      this.reviewAnimationFrame = window.requestAnimationFrame(this.stepReviewAnimation);
    },
    getFirstReviewCardDistance() {
      const cardRefs = this.$refs.reviewCards;
      const firstCard = Array.isArray(cardRefs) ? cardRefs[0] : cardRefs;
      if (!firstCard) {
        return 0;
      }
      return firstCard.offsetHeight + 16;
    },
    playReviewVideos() {
      const videoRefs = this.$refs.reviewVideos;
      const videoList = Array.isArray(videoRefs) ? videoRefs : videoRefs ? [videoRefs] : [];
      videoList.forEach((video) => {
        if (!video || typeof video.play !== "function") {
          return;
        }
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      });
    }
  }
};
</script>

<style lang="less" scoped>
.reputation-hero {
  background:
    linear-gradient(90deg, rgba(3, 23, 50, 0.94) 0%, rgba(7, 55, 105, 0.74) 48%, rgba(12, 83, 146, 0.34) 100%),
    var(--page-banner-image) center / cover no-repeat;
}

.content-section {
  background: var(--soft-blue);
}

.page-shell {
  width: min(1280px, 100%);
  margin: 0 auto;
}

.reputation-layout {
  display: grid;
  grid-template-columns: minmax(380px, 0.6fr) minmax(0, 0.64fr);
  gap: 20px;
}

.score-panel {
  padding: 26px;
}

.score-panel b {
  display: block;
  margin-bottom: 8px;
  color: var(--blue);
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.score-panel h3 {
  margin: 0 0 12px;
  font-size: 24px;
}

.score-panel p {
  margin: 0;
  color: var(--muted);
}

.pdf-preview {
  display: grid;
  gap: 12px;
  margin-top: 22px;
}

.pdf-preview iframe {
  width: 100%;
  height: 620px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #f5f9ff;
}

.pdf-link {
  justify-self: start;
  display: inline-flex;
  min-height: 38px;
  align-items: center;
  padding: 0 16px;
  border-radius: 999px;
  background: linear-gradient(135deg, #1d68c7, #0b4c9a);
  color: var(--white);
  font-size: 13px;
  font-weight: 900;
}

.score-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 20px;
}

.score-grid article {
  padding: 14px;
  border-radius: 10px;
  background: #f5f9ff;
}

.score-grid strong {
  display: block;
}

.score-grid span {
  color: var(--muted);
  font-size: 13px;
}

.review-stream {
  height: 780px;
  overflow: hidden;
}

.review-track {
  display: grid;
  gap: 16px;
  will-change: transform;
}

.review-stream--static {
  height: auto;
  overflow: visible;
}

.review-card {
  padding: 16px;
}

.review-video {
  width: 100%;
  display: block;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 10px;
}

.video-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
}

.video-info h3 {
  margin: 0;
  font-size: 18px;
}

.video-info span {
  color: var(--muted);
  font-size: 12px;
  text-transform: uppercase;
}

@media (max-width: 1040px) {
  .reputation-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 680px) {
  .score-panel,
  .review-card {
    padding: 18px;
  }

  .score-panel h3 {
    font-size: 20px;
  }

  .pdf-preview iframe {
    height: 420px;
  }

  .review-stream {
    height: 620px;
  }

  .video-info {
    align-items: flex-start;
    flex-direction: column;
    gap: 6px;
  }

  .video-info h3 {
    font-size: 16px;
  }
}
</style>
